/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './silo-dashboard-widget.scss';

/* eslint-disable import/no-unresolved, import/default */

import siloDashboardWidgetTemplate from './silo-dashboard-widget.tpl.html';

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.siloDashboardWidget', [])
    .directive('agSiloDashboardWidget', SiloDashboardWidget)
    .name;

/*@ngInject*/
function SiloDashboardWidget() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            siloId: '=',
            sensorsView: '<'
        },
        controller: SiloDashboardWidgetController,
        controllerAs: 'vm',
        templateUrl: siloDashboardWidgetTemplate
    };
}

/*@ngInject*/
function SiloDashboardWidgetController($scope, $log, $q, $translate, unitService, siloService, entityService, agTypes, types) {

    let vm = this;

    vm.loading = true;
    vm.agTypes = agTypes;

    vm.tempHistoryUnits = unitService.temperatureUnitSymbol();

    vm.sensorsViewTypes = {
        temperature: "ag:thermometer",
        moisture: "ag:moisture",
        cropLevel: "ag:crop_level",
        headSpace: "ag:head_space"
    };

    vm.latestValuesSubscriptionOptions = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError,
            dataLoading: function(subscription) {
                vm.loading = subscription.loadingData;
            }
        },
        useDashboardTimewindow: false
    };

    vm.flotSettings = {
        shadowSize: 0,
        yaxis: {
            showLabels: false
        },
        xaxis: {
            showLabels: false
        },
        grid: {
            outlineWidth: 0,
            margin: 0,
            minBorderMargin: 0,
            tickColor: 'rgba(255, 255, 255, 0)' // Workaround to hide grid lines
        }
    };

    vm.flotKeySettings = {
        lineWidth: 1
    };

    vm.tempColor = tempColor;
    vm.deltaTempColor = deltaTempColor;
    vm.moistureColor = moistureColor;
    vm.deltaMoistureColor = deltaMoistureColor;
    vm.cropLevelColor = cropLevelColor;
    vm.headSpaceColor = headSpaceColor;

    $scope.$watch('vm.siloId', function() {
        vm.silo = null;
        reset();
        if (vm.siloId) {
            vm.loading = true;
            siloService.getSilo(vm.siloId).then(
                function success(silo) {
                    vm.silo = silo;
                    vm.maxTemperature = vm.silo.maxTemperature || 24;
                    vm.maxDeltaTemperature = vm.silo.maxDeltaTemperature || 1;
                    vm.maxMoisture =  vm.silo.maxMoisture || 24;
                    vm.maxDeltaMoisture = vm.silo.maxDeltaMoisture || 1;
                    vm.maxCO2 =  vm.silo.maxCO2 || 24;
                    vm.maxDeltaCO2 = vm.silo.maxDeltaCO2 || 1;
                    if (vm.silo.maxCropLevelThreshold) {
                        vm.maxCropLevel = vm.silo.maxCropLevelThreshold;
                    }
                    if (vm.silo.minCropLevelThreshold) {
                        vm.minCropLevel = vm.silo.minCropLevelThreshold;
                    }
                    createLatestValuesSubscription();
                    updateChartSubscription();
                },
                function fail() {
                    vm.loading = false;
                }
            );
        }
    });

    $scope.$watch('vm.sensorsView', (newValue, prevValue) => {
        if (prevValue && newValue && vm.silo) {
            let devicePromise;
            vm.loading = true;
            if (newValue === vm.sensorsViewTypes.cropLevel) {
                devicePromise = entityService.getRelatedEntities(vm.siloId, types.entityType.device, [agTypes.deviceType.levelIndicator], 1, null, agTypes.deviceTypeTranslatePrefix);
            } else if (newValue === vm.sensorsViewTypes.headSpace) {
                devicePromise = entityService.getRelatedEntities(vm.siloId, types.entityType.device, [agTypes.deviceType.headSpaceSensor], 1, null, agTypes.deviceTypeTranslatePrefix);
            } else {
                devicePromise = $q.when(null);
            }
            devicePromise.then(
                (devicePayload) => {
                    let device = (devicePayload && devicePayload.length) ? devicePayload[0] : null;
                    reset();
                    createLatestValuesSubscription(device);
                    updateChartSubscription(device);
                    vm.loading = false;
                }
            );
        }
    });

    function createLatestValuesSubscription(device) {
        let latestValuesSubscriptionsInfo;
        if (vm.sensorsView === vm.sensorsViewTypes.temperature || vm.sensorsView === vm.sensorsViewTypes.moisture) {
            latestValuesSubscriptionsInfo = [{
                type: types.datasourceType.entity,
                entityType: types.entityType.asset,
                entityId: vm.silo.id.id,
                entityName: vm.silo.name
            }];
            if (vm.sensorsView === vm.sensorsViewTypes.temperature) {
                latestValuesSubscriptionsInfo[0].timeseries = [
                    {name: vm.agTypes.telemetry.silo.currentMaxTemperature},
                    {name: vm.agTypes.telemetry.silo.currentMaxDeltaTemperature}
                ];
            } else if (vm.sensorsView === vm.sensorsViewTypes.moisture) {
                latestValuesSubscriptionsInfo[0].timeseries = [
                    {name: vm.agTypes.telemetry.silo.currentMaxMoisture},
                    {name: vm.agTypes.telemetry.silo.currentMaxDeltaMoisture}
                ];
            }
        } else if (device && (vm.sensorsView === vm.sensorsViewTypes.cropLevel || vm.sensorsView === vm.sensorsViewTypes.headSpace)) {
            latestValuesSubscriptionsInfo = [{
                type: types.datasourceType.entity,
                entityType: types.entityType.device,
                entityId: device.id.id,
                entityName: device.name
            }];
            if (vm.sensorsView === vm.sensorsViewTypes.cropLevel) {
                latestValuesSubscriptionsInfo[0].timeseries = [
                    {name: agTypes.telemetry.levelIndicator.cropLevelPrefix + 1}
                ];
            } else if (vm.sensorsView === vm.sensorsViewTypes.headSpace) {
                latestValuesSubscriptionsInfo[0].timeseries = [
                    {name: vm.agTypes.telemetry.headSpaceSensor.co2level},
                    {name: vm.agTypes.telemetry.headSpaceSensor.temperature},
                    {name: vm.agTypes.telemetry.headSpaceSensor.humidity}
                ];
            }
        }
        vm.latestValuesSubscriptionOptions.timeWindowConfig = angular.copy(vm.ctx.dashboardTimewindow);
        vm.latestValuesSubscriptionOptions.timeWindowConfig.aggregation.type = types.aggregation.none.value;
        vm.latestValuesSubscriptionOptions.timeWindowConfig.aggregation.limit = 1;
        vm.ctx.subscriptionApi.createSubscriptionFromInfo (
            types.widgetType.timeseries.value, latestValuesSubscriptionsInfo, vm.latestValuesSubscriptionOptions, false, true).then(
            (subscription) => {
                vm.latestValuesSubscription = subscription;
            }
        );
    }

    function updateChartSubscription(device) {
        if (vm.sensorsView === vm.sensorsViewTypes.temperature || vm.sensorsView === vm.sensorsViewTypes.moisture) {
            vm.chartSubscriptionsInfo = [{
                type: types.datasourceType.entity,
                entityType: types.entityType.asset,
                entityId: vm.siloId.id,
                entityName: vm.silo.name
            }];
            if (vm.sensorsView === vm.sensorsViewTypes.temperature) {
                vm.chartSubscriptionsInfo[0].timeseries = [
                    {
                        name: agTypes.telemetry.silo.currentMaxTemperature,
                        label: $translate.instant('ag.silo.max-temperature'),
                        color: agTypes.colors.maxColor,
                        postFuncBody: unitService.temperaturePostprocessingFuncBody()
                    }
                ];
            } else if (vm.sensorsView === vm.sensorsViewTypes.moisture) {
                vm.chartSubscriptionsInfo[0].timeseries = [
                    {
                        name: agTypes.telemetry.silo.currentMaxMoisture,
                        label: $translate.instant('ag.silo.max-moisture'),
                        color: agTypes.colors.minColor
                    }
                ];
            }
        }  else if (device && (vm.sensorsView === vm.sensorsViewTypes.cropLevel || vm.sensorsView === vm.sensorsViewTypes.headSpace)) {
            vm.chartSubscriptionsInfo = [{
                type: types.datasourceType.entity,
                entityType: types.entityType.device,
                entityId: device.id.id,
                entityName: device.name
            }];
            if (vm.sensorsView === vm.sensorsViewTypes.cropLevel) {
                vm.chartSubscriptionsInfo[0].timeseries = [
                    {
                        name: agTypes.telemetry.levelIndicator.cropLevelPrefix + 1,
                        label: $translate.instant('ag.silo.crop-level'),
                        color: "#8d6e63"
                    }
                ];
            } else if (vm.sensorsView === vm.sensorsViewTypes.headSpace) {
                vm.chartSubscriptionsInfo[0].timeseries = [
                    {
                        name: agTypes.telemetry.headSpaceSensor.co2level,
                        label: $translate.instant('ag.silo.head-space-co2level'),
                        color: agTypes.colors.mainGrey
                    }
                ];
            }
        }
    }

    function tempColor(val) {
        if (val >= vm.maxTemperature) {
            return agTypes.colors.errorColor;
        } else {
            return agTypes.colors.normalGreyColor;
        }
    }

    function deltaTempColor(val) {
        if (val >= vm.maxDeltaTemperature) {
            return agTypes.colors.errorColor;
        } else {
            return agTypes.colors.normalGreyColor;
        }
    }

    function moistureColor(val) {
        if (val >= vm.maxMoisture) {
            return agTypes.colors.errorColor;
        } else {
            return agTypes.colors.normalGreyColor;
        }
    }

    function deltaMoistureColor(val) {
        if (val >= vm.maxDeltaMoisture) {
            return agTypes.colors.errorColor;
        } else {
            return agTypes.colors.normalGreyColor;
        }
    }

    function cropLevelColor(val) {
        if (angular.isUndefined(vm.maxCropLevel) && angular.isUndefined(vm.minCropLevel)) {
            return agTypes.colors.normalGreyColor;
        }
        if ((angular.isDefined(vm.maxCropLevel) && (val >= vm.maxCropLevel)) ||
            (angular.isDefined(vm.minCropLevel) && (val <= vm.minCropLevel))) {
            return agTypes.colors.errorColor;
        } else {
            return agTypes.colors.normalGreyColor;
        }
    }

    //temp function
    function headSpaceColor() {
        return agTypes.colors.normalGreyColor
    }

    $scope.$on('dashboardTimewindowChanged', function (event, newDashboardTimewindow) {
        if (vm.latestValuesSubscription) {
            var newTimewindowConfig = angular.copy(newDashboardTimewindow);
            newTimewindowConfig.aggregation.type = types.aggregation.none.value;
            newTimewindowConfig.aggregation.limit = 1;
            vm.latestValuesSubscription.timeWindowConfig = newTimewindowConfig;
        }
    });

    $scope.$on('$destroy', function() {
        reset();
    });

    function reset() {
        if (vm.latestValuesSubscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.latestValuesSubscription.id);
        }
        resetValues();
    }

    function resetValues() {
        vm.maxTempVal = 0;
        vm.maxDeltaTempVal = 0;
        vm.maxTemp = '';
        vm.maxDeltaTemp = '';

        vm.maxMosVal = 0;
        vm.maxDeltaMosVal = 0;
        vm.maxMos = '';
        vm.maxDeltaMos = '';

        vm.cropLevelValue = 0;
        vm.cropLevel = '';

        vm.headSpaceCo2Value = 0;
        vm.headSpaceTempValue = 0;
        vm.headSpaceMosValue = 0;
        vm.headSpaceCo2 = '';
        vm.headSpaceTemp = '';
        vm.headSpaceMos = '';
    }

    function onDataUpdated(subscription, apply) {
        var data = subscription.data;
        for (var i=0;i<data.length;i++) {
            var datasourceData = data[i];
            var val = getNumberValue(datasourceData);
            switch (datasourceData.dataKey.name) {
                case vm.agTypes.telemetry.silo.currentMaxTemperature:
                    vm.maxTempVal = val;
                    vm.maxTemp = vm.ctx.utils.formatValue(unitService.toUserTemperature(val), 0, '')+'°';
                    break;
                case vm.agTypes.telemetry.silo.currentMaxDeltaTemperature:
                    vm.maxDeltaTempVal = val;
                    vm.maxDeltaTemp = vm.ctx.utils.formatValue(unitService.toUserTemperature(val, true), 1, '')+'°';
                    break;
                case vm.agTypes.telemetry.silo.currentMaxMoisture:
                    vm.maxMosVal = val;
                    vm.maxMos = val.toFixed(0) + '%';
                    break;
                case vm.agTypes.telemetry.silo.currentMaxDeltaMoisture:
                    vm.maxDeltaMosVal = val;
                    vm.maxDeltaMos = parseFloat(val.toFixed(1)) + '%';
                    break;
                case vm.agTypes.telemetry.levelIndicator.cropLevelPrefix + 1:
                    vm.cropLevelValue = val;
                    vm.cropLevel = parseFloat(val.toFixed(1)) + 'm';
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.co2level:
                    vm.headSpaceCo2Value = val;
                    vm.headSpaceCo2 = val.toFixed(0);
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.temperature:
                    vm.headSpaceTempValue = val;
                    vm.headSpaceTemp = vm.ctx.utils.formatValue(unitService.toUserTemperature(val), 1, '')+'°';
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.humidity:
                    vm.headSpaceMosValue = val;
                    vm.headSpaceMos = val.toFixed(1) + '%';
                    break;
            }
        }
        if (apply) {
            $scope.$digest();
        }
    }

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }

    function getNumberValue(keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][1])) {
            if (!isNaN(keyData.data[0][1])) {
                return Number(keyData.data[0][1]);
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    }

}